import type { CourseMindItemSearchResult } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { fetchNewCourses } from '../../../actions/course.actions';
import NewCarousel from '../../../components/Carousel/NewCarousel';
import { numberOfSlides, routes } from '../../../constants';
import { getNewCourses, getNewCoursesStatus } from '../../../reducers/dashboard.selectors';
import { isSchoolSubdomain } from '../../../utils/subdomain';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import CardSkeleton from '../../Carousel/CardSkeleton';
import { getCourseMuiCardProps } from '../../cards/MuiCard/CourseMuiCard/utils';
import DashboardMuiCard from '../../cards/MuiCard/DashboardMuiCard';

const CoursesCarousel: React.FC = () => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const fetchNewCoursesAction = useAction(fetchNewCourses);
	const newCourses = useSelector((state) => getNewCourses(state));
	const newCoursesStatus = useSelector((state) => getNewCoursesStatus(state));
	const slides = new Array(numberOfSlides.xl).fill(null);
	const isSchulenUser = isSchoolSubdomain(window.location.hostname);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (isSchulenUser) return;
		fetchNewCoursesAction();
	}, []);

	if (isSchulenUser) return null;
	if (newCourses.length < numberOfSlides.xl) return null;

	return (
		<NewCarousel
			title={t('New in the course catalog')}
			onShowAll={() => router.push(`/${routes.courseCatalog.root}`)}
		>
			{newCoursesStatus === AsyncActionStatus.READY
				? newCourses.map((course: CourseMindItemSearchResult, index: number) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: index keys are pre-existing; switching the key source changes reconciliation behavior
						<DashboardMuiCard key={index} {...getCourseMuiCardProps(course)} />
					))
				: // biome-ignore lint/suspicious/noArrayIndexKey: index keys are pre-existing; switching the key source changes reconciliation behavior
					slides.map((_, index) => <CardSkeleton key={index} />)}
		</NewCarousel>
	);
};

export default React.memo(CoursesCarousel);
